// Pure chart/format helpers for the performance analytics panel — no I/O, no
// React, so the math is unit-testable without rendering or fetch stubs.

export const TIMEFRAME_LABELS = ['1D', '3D', '1W', '1M', '3M', '6M', '1Y', 'ALL'] as const;
export type TimeframeLabel = (typeof TIMEFRAME_LABELS)[number];

export interface EquityPoint {
  t: number;
  equity: number;
}

export interface TimeframeStats {
  trade_count: number;
  wins: number;
  losses: number;
  win_rate_pct: number;
  profit_factor: number | null;
  sharpe_ratio: number;
  max_drawdown_pct: number;
  avg_duration_seconds: number;
  net_pnl_usd: number;
  equity_curve: EquityPoint[];
  trade_pnls: number[];
  pnl_bins?: HistogramBin[];
}

export interface PerformancePayload {
  generated_at: number;
  current_equity: number;
  timeframes: Record<string, TimeframeStats>;
}

export interface HistogramBin {
  from: number;
  to: number;
  count: number;
  // Bin midpoint sign decides the diverging pole (win vs loss color).
  isGain: boolean;
}

/** Bucket per-trade PnLs into `binCount` equal-width bins across their range. */
export function binPnls(pnls: number[], binCount: number): HistogramBin[] {
  if (pnls.length === 0 || binCount <= 0) return [];
  const min = Math.min(...pnls);
  const max = Math.max(...pnls);
  const width = (max - min) / binCount || 1; // all-equal PnLs: one 1-wide bin span
  const bins: HistogramBin[] = Array.from({ length: binCount }, (_, i) => ({
    from: min + i * width,
    to: min + (i + 1) * width,
    count: 0,
    isGain: min + (i + 0.5) * width >= 0,
  }));
  for (const pnl of pnls) {
    const index = Math.min(Math.floor((pnl - min) / width), binCount - 1);
    bins[index].count += 1;
  }
  return bins;
}

/**
 * Scale equity points into an SVG polyline points string within width/height,
 * with `pad` px of breathing room. Y grows downward in SVG, so higher equity
 * maps to a smaller y. A flat/single-point series renders mid-height.
 */
export function equityToPolylinePoints(
  curve: EquityPoint[],
  width: number,
  height: number,
  pad = 4,
): string {
  if (curve.length === 0) return '';
  const ts = curve.map((p) => p.t);
  const eqs = curve.map((p) => p.equity);
  const tMin = Math.min(...ts);
  const tSpan = Math.max(...ts) - tMin || 1;
  const eqMin = Math.min(...eqs);
  const eqSpan = Math.max(...eqs) - eqMin || 1;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  return curve
    .map((p) => {
      const x = pad + ((p.t - tMin) / tSpan) * innerW;
      const y = pad + innerH - ((p.equity - eqMin) / eqSpan) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

/** "1h 24m" / "3d 4h" / "45s" — compact trade-duration formatting. */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) return '—';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.round(seconds)}s`;
}

/** Signed compact USD: "+$1,234.50" / "-$21.37" / "$0.00". */
export function formatSignedUsd(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Profit factor renders "∞-free": null → em-dash (undefined, no losses yet). */
export function formatProfitFactor(value: number | null): string {
  return value === null ? '—' : value.toFixed(2);
}
